import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Volume2, VolumeX, Sparkles, Heart, Turtle, Rabbit, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import BadgeCelebration from '@/components/BadgeCelebration';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import { useReadSpeed, useKaraoke, splitSentences, splitWords, SPEED_PRESETS } from '@/hooks/useReadSpeed';

export default function SELLessonPage() {
  const { childId, lessonId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('story'); // story -> quiz -> done
  const [selected, setSelected] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [completeResult, setCompleteResult] = useState(null);
  const [celebrationBadge, setCelebrationBadge] = useState(null);

  // Speed Memory + Karaoke — same behaviour as Summer Slide.
  const { stop: stopStory, isSpeaking: isReading, speakChunks } = useElevenLabsTTS({ voice: 'shimmer', speed: 0.95 });
  const { readSpeed, cycleReadSpeed } = useReadSpeed(childId);
  const { highlightedSentence, highlightedWord, playPassage, stopPassage } = useKaraoke({
    speakChunks,
    stopStory,
    isReading,
    readSpeed,
  });

  // One-shot short-line speak (feedback, prompts) — falls back to browser TTS
  // since ElevenLabs chunked reader is reserved for narrative passages.
  const speakLine = useCallback((text) => {
    try {
      if (!('speechSynthesis' in window) || !text) return;
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      utter.pitch = 1.1;
      window.speechSynthesis.speak(utter);
    } catch (err) { void err; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/sel/lessons/${lessonId}`);
        if (cancelled) return;
        setLesson(res.data);
        // Auto-read the story with karaoke highlighting after a short delay.
        setTimeout(() => {
          if (!cancelled && res.data?.story) playPassage(res.data.story);
        }, 350);
      } catch (err) {
        toast.error('Lesson not found');
        navigate(`/sel/${childId}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      stopPassage();
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (err) { void err; }
    };
    // playPassage/stopPassage identities are stable per-render but not memoized;
    // we intentionally load once per lessonId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, api, childId, navigate]);

  const handleSelect = async (option) => {
    if (selected && selected.correct) return;
    setSelected(option);
    setAttempts((count) => count + 1);
    speakLine(option.feedback);

    if (option.correct) {
      try {
        const res = await api.post('/sel/complete', {
          child_id: childId,
          lesson_id: lessonId,
          correct: attempts === 0,
        });
        setCompleteResult(res.data);
        setStep('done');
        if (res.data.badge_awarded) {
          setCelebrationBadge({ id: res.data.badge_awarded });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'unknown';
        console.error('SEL complete failed:', err?.response?.status, detail, err);
        toast.error(`Could not save progress (${detail})`);
      }
    }
  };

  if (loading || !lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  const storySentences = splitSentences(lesson.story || '');

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-amber-50">
      <BadgeCelebration badge={celebrationBadge} onClose={() => setCelebrationBadge(null)} />
      <header className="bg-white/80 backdrop-blur-md border-b border-rose-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" className="rounded-full shrink-0" onClick={() => { stopPassage(); navigate(`/sel/${childId}`); }} data-testid="sel-lesson-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Lesson {lesson.order}</p>
              <h1 className="font-fredoka font-bold text-lg text-slate-800 truncate">{lesson.title}</h1>
            </div>
          </div>
          <div className="text-3xl shrink-0">{lesson.emoji}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 pb-24">
        <AnimatePresence mode="wait">
          {step === 'story' && (
            <motion.div
              key="story"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              <Card className={`overflow-hidden border-0 shadow-lg bg-gradient-to-br ${lesson.color} text-white`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-6xl shrink-0">{lesson.emoji}</div>
                    <div>
                      <p className="text-xs uppercase tracking-wide opacity-80">Magic phrase</p>
                      <p className="text-2xl font-fredoka font-bold">&ldquo;{lesson.phrase}&rdquo;</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  {/* Karaoke-highlighted story text — audit follow-up: word-level tracking
                      matches Summer Slide behaviour. */}
                  <p className="text-lg text-slate-800 leading-relaxed" data-testid="sel-story-text">
                    {storySentences.length === 0 ? (
                      lesson.story
                    ) : (
                      storySentences.map((s, i) => {
                        const isActive = i === highlightedSentence;
                        if (!isActive) {
                          return (
                            <span key={i} data-testid={`sel-story-sentence-${i}`}>
                              {s}{i < storySentences.length - 1 ? ' ' : ''}
                            </span>
                          );
                        }
                        const tokens = splitWords(s);
                        return (
                          <span
                            key={i}
                            data-testid={`sel-story-sentence-${i}`}
                            className="bg-amber-100 rounded-md shadow-inner ring-2 ring-amber-200 px-1 py-0.5"
                          >
                            {tokens.map((tok, wi) => {
                              const isWordActive = wi === highlightedWord;
                              return (
                                <span
                                  key={wi}
                                  data-testid={isWordActive ? `sel-story-word-active` : undefined}
                                  className={isWordActive ? 'bg-rose-400 text-white rounded px-0.5' : ''}
                                >
                                  {tok}
                                </span>
                              );
                            })}
                            {i < storySentences.length - 1 ? ' ' : ''}
                          </span>
                        );
                      })
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    {/* Speed toggle — persisted per child profile via useReadSpeed */}
                    <button
                      type="button"
                      onClick={cycleReadSpeed}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border-2 border-rose-200 text-rose-700 text-sm hover:bg-rose-50"
                      aria-label={`Reading speed: ${SPEED_PRESETS[readSpeed].label}. Tap to change.`}
                      title={`Reading speed: ${SPEED_PRESETS[readSpeed].label}. Tap to cycle slow / normal / fast.`}
                      data-testid="sel-read-speed"
                    >
                      {readSpeed === 'slow' ? (
                        <Turtle className="w-4 h-4" />
                      ) : readSpeed === 'normal' ? (
                        <Rabbit className="w-4 h-4" />
                      ) : (
                        <><Rabbit className="w-4 h-4" /><Zap className="w-3 h-3 -ml-1 text-orange-500" /></>
                      )}
                      <span>{SPEED_PRESETS[readSpeed].label}</span>
                    </button>
                    {isReading ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                        onClick={stopPassage}
                        data-testid="sel-story-tts-stop"
                      >
                        <VolumeX className="w-4 h-4 mr-1" /> Stop reading
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => playPassage(lesson.story)}
                        data-testid="sel-listen-story"
                      >
                        <Volume2 className="w-4 h-4 mr-1" /> Listen again
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="rounded-full bg-rose-500 hover:bg-rose-600 ml-auto"
                      onClick={() => { stopPassage(); speakLine(lesson.scenario.prompt); setStep('quiz'); }}
                      data-testid="sel-start-quiz"
                    >
                      What should they do? →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {step === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <h2 className="text-xl font-fredoka font-bold text-slate-800" data-testid="sel-quiz-prompt">
                      {lesson.scenario.prompt}
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full shrink-0"
                      onClick={() => speakLine(lesson.scenario.prompt)}
                      aria-label="Listen"
                    >
                      <Volume2 className="w-5 h-5 text-slate-500" />
                    </Button>
                  </div>

                  <div className="space-y-3" data-testid="sel-quiz-options">
                    {lesson.scenario.options.map((option, idx) => {
                      const chosen = selected && selected.id === option.id;
                      const showResult = !!selected;
                      const isCorrect = option.correct;
                      let stateClasses = 'border-slate-200 hover:border-rose-400 hover:bg-rose-50';
                      if (showResult && chosen && isCorrect) stateClasses = 'border-emerald-400 bg-emerald-50';
                      if (showResult && chosen && !isCorrect) stateClasses = 'border-amber-400 bg-amber-50';
                      if (showResult && !chosen && isCorrect) stateClasses = 'border-emerald-300 bg-emerald-50/70';
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handleSelect(option)}
                          className={`w-full text-left p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${stateClasses}`}
                          data-testid={`sel-quiz-option-${idx}`}
                        >
                          <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 shrink-0">
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <span className="flex-1 text-slate-800">{option.text}</span>
                          {showResult && chosen && isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                          {showResult && chosen && !isCorrect && <XCircle className="w-5 h-5 text-amber-500" />}
                        </button>
                      );
                    })}
                  </div>

                  {selected && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-4 p-4 rounded-xl ${selected.correct ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}
                      data-testid="sel-quiz-feedback"
                    >
                      <p className="font-medium">{selected.feedback}</p>
                      {!selected.correct && (
                        <p className="text-sm mt-1 opacity-80">Tap another answer to try again.</p>
                      )}
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {step === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <Card className="border-0 shadow-lg overflow-hidden bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 text-white">
                <CardContent className="p-8 text-center">
                  <motion.div
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="text-7xl mb-3"
                  >
                    🎉
                  </motion.div>
                  <h2 className="text-2xl font-fredoka font-bold mb-2">You did it!</h2>
                  <p className="opacity-90 mb-4">You learned how to say &ldquo;{lesson.phrase}&rdquo;</p>
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    {completeResult?.xp_awarded > 0 && (
                      <div className="bg-white/20 rounded-full px-4 py-2 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-bold">+{completeResult.xp_awarded} XP</span>
                      </div>
                    )}
                    {completeResult?.badge_awarded && (
                      <div className="bg-white/20 rounded-full px-4 py-2 flex items-center gap-2">
                        <Heart className="w-5 h-5 fill-white" />
                        <span className="font-bold">New Badge!</span>
                      </div>
                    )}
                    {completeResult?.already_completed && (
                      <div className="bg-white/20 rounded-full px-4 py-2">
                        <span className="text-sm">Already learned — great review!</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate(`/sel/${childId}`)}
                  data-testid="sel-back-to-list"
                >
                  Back to lessons
                </Button>
                <Button
                  className="rounded-full bg-rose-500 hover:bg-rose-600"
                  onClick={() => {
                    api.get('/sel/lessons').then((res) => {
                      const lessons = res.data.lessons || [];
                      const idx = lessons.findIndex((entry) => entry.id === lessonId);
                      const nextLesson = idx >= 0 ? lessons[idx + 1] : null;
                      if (nextLesson) {
                        navigate(`/sel/${childId}/lesson/${nextLesson.id}`);
                        window.location.reload();
                      } else {
                        toast.success('You finished the whole kindness journey! 🌟');
                        navigate(`/sel/${childId}`);
                      }
                    });
                  }}
                  data-testid="sel-next-lesson"
                >
                  Next lesson →
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
